import type { JSONContent } from "@tiptap/core";
import {
  deriveLoreScopeBadge,
  emptyLoreFilters,
  isLoreEntryTypeImageGenerationEligible,
  isScopedLoreType,
  loreCanonStatusLabels,
  loreEntryTypeLabels,
  projectDocumentBadgeLabels,
  projectDocumentDescriptionLabels,
  projectDocumentTitleLabels,
  type GovernanceDocRecord,
  type LoreCanonStatus,
  type LoreEntryFilters,
  type LoreEntryRecord,
  type LoreEntryType,
  type LoreEntryView,
  type ProjectDocumentKind,
  type ProjectDocumentView,
  type ProjectLoreCapacitySummary,
  type StorySpineRecord,
} from "@/types/lore";
import type { SubscriptionTier } from "@/types/projects";

const EMPTY_PARAGRAPH: JSONContent = {
  type: "paragraph",
};

const LOCAL_RULES_HEADING = "Local Rules";

function getTextContent(node: JSONContent): string {
  if (typeof node.text === "string") {
    return node.text;
  }

  return (node.content ?? []).map((child) => getTextContent(child)).join("");
}

function isLocalRulesHeading(node: JSONContent) {
  return (
    node.type === "heading"
    && getTextContent(node).trim().toLowerCase() === LOCAL_RULES_HEADING.toLowerCase()
  );
}

function normalizeNodes(nodes: JSONContent[] | undefined) {
  return nodes && nodes.length > 0 ? nodes : [EMPTY_PARAGRAPH];
}

export function cloneEmptyEditorDocument(): JSONContent {
  return {
    type: "doc",
    content: [EMPTY_PARAGRAPH],
  };
}

export function normalizeEditorDocument(content: JSONContent | null | undefined): JSONContent {
  if (!content || content.type !== "doc") {
    return cloneEmptyEditorDocument();
  }

  if (!Array.isArray(content.content) || content.content.length === 0) {
    return cloneEmptyEditorDocument();
  }

  return content;
}

export function isEditorDocumentEmpty(content: JSONContent | null | undefined) {
  const document = normalizeEditorDocument(content);
  const nodes = document.content ?? [];

  if (nodes.length === 0) {
    return true;
  }

  return nodes.every((node) => {
    if (node.type !== "paragraph") {
      return false;
    }

    return getTextContent(node).trim().length === 0;
  });
}

export function splitLoreDocumentSections(
  type: LoreEntryType,
  body: JSONContent | null | undefined,
) {
  const normalized = normalizeEditorDocument(body);

  if (!isScopedLoreType(type)) {
    return {
      mainBody: normalized,
      localRules: cloneEmptyEditorDocument(),
    };
  }

  const nodes = normalized.content ?? [];
  const localRulesHeadingIndex = nodes.findIndex(isLocalRulesHeading);

  if (localRulesHeadingIndex === -1) {
    return {
      mainBody: normalized,
      localRules: cloneEmptyEditorDocument(),
    };
  }

  return {
    mainBody: {
      type: "doc",
      content: normalizeNodes(nodes.slice(0, localRulesHeadingIndex)),
    },
    localRules: {
      type: "doc",
      content: normalizeNodes(nodes.slice(localRulesHeadingIndex + 1)),
    },
  };
}

export function buildLoreDocumentBody(
  type: LoreEntryType,
  mainBody: JSONContent | null | undefined,
  localRules: JSONContent | null | undefined,
) {
  const normalizedMainBody = normalizeEditorDocument(mainBody);

  if (!isScopedLoreType(type)) {
    return normalizedMainBody;
  }

  const normalizedLocalRules = normalizeEditorDocument(localRules);

  if (isEditorDocumentEmpty(normalizedLocalRules)) {
    return normalizedMainBody;
  }

  return {
    type: "doc",
    content: [
      ...(normalizedMainBody.content ?? []),
      {
        type: "heading",
        attrs: {
          level: 2,
        },
        content: [
          {
            type: "text",
            text: LOCAL_RULES_HEADING,
          },
        ],
      },
      ...(normalizedLocalRules.content ?? []),
    ],
  } satisfies JSONContent;
}

export function buildProjectDocumentView(
  kind: ProjectDocumentKind,
  row: GovernanceDocRecord | StorySpineRecord,
): ProjectDocumentView {
  return {
    id: row.id,
    kind,
    body: normalizeEditorDocument(row.body as JSONContent),
    updatedAt: row.updated_at,
    titleKey: projectDocumentTitleLabels[kind],
    badgeKeys: projectDocumentBadgeLabels[kind],
    descriptionKey: projectDocumentDescriptionLabels[kind],
  };
}

export function mapLoreEntryRecordToView(
  record: LoreEntryRecord,
  coverImageUrl: string | null,
): LoreEntryView {
  const fullBody = normalizeEditorDocument(record.body as JSONContent);
  const { localRules, mainBody } = splitLoreDocumentSections(record.type, fullBody);
  const coverGenerationEligible = isLoreEntryTypeImageGenerationEligible(record.type);

  return {
    id: record.id,
    projectId: record.project_id,
    type: record.type,
    title: record.title,
    body: fullBody,
    mainBody,
    localRules,
    canonStatus: record.canon_status,
    coverImagePath: record.cover_image_path,
    coverImageUrl,
    createdAt: record.created_at,
    updatedAt: record.updated_at,
    typeLabelKey: loreEntryTypeLabels[record.type],
    canonStatusLabelKey: loreCanonStatusLabels[record.canon_status],
    scopeBadgeKey: deriveLoreScopeBadge(record.type),
    coverGenerateActionLabel: coverGenerationEligible ? "lore.actions.generateImage" : null,
    coverGenerationEligible,
    hasCoverImage: Boolean(record.cover_image_path),
    hasLocalRulesSection: isScopedLoreType(record.type),
  };
}

export function sortLoreEntriesByUpdatedAt(entries: LoreEntryView[]) {
  return [...entries].sort(
    (left, right) =>
      new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime(),
  );
}

export function upsertLoreEntry(entries: LoreEntryView[], entry: LoreEntryView) {
  const nextEntries = entries.filter((candidate) => candidate.id !== entry.id);
  nextEntries.push(entry);
  return sortLoreEntriesByUpdatedAt(nextEntries);
}

export function removeLoreEntry(entries: LoreEntryView[], entryId: string) {
  return entries.filter((entry) => entry.id !== entryId);
}

export function applyLoreFilters(entries: LoreEntryView[], filters: LoreEntryFilters) {
  return sortLoreEntriesByUpdatedAt(
    entries.filter((entry) => {
      const matchesType =
        filters.typeFilter === emptyLoreFilters.typeFilter || entry.type === filters.typeFilter;
      const matchesStatus =
        filters.statusFilter === emptyLoreFilters.statusFilter
        || entry.canonStatus === filters.statusFilter;
      const normalizedSearch = filters.searchText.trim().toLowerCase();
      const matchesSearch =
        normalizedSearch.length === 0 || entry.title.toLowerCase().includes(normalizedSearch);

      return matchesType && matchesStatus && matchesSearch;
    }),
  );
}

export function buildLoreTextSearchQuery(searchText: string) {
  const tokens = searchText
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map((token) => token.replace(/[^a-z0-9_-]/g, ""))
    .filter(Boolean);

  return tokens.map((token) => `${token}:*`).join(" & ");
}

export function mapLoreCapacityRow(input: {
  tier: SubscriptionTier;
  lore_entries_used: number;
  lore_entries_limit: number | null;
  lore_entries_remaining: number | null;
  is_unlimited: boolean;
}): ProjectLoreCapacitySummary {
  return {
    tier: input.tier,
    loreEntriesUsed: input.lore_entries_used,
    loreEntriesLimit: input.lore_entries_limit,
    loreEntriesRemaining: input.lore_entries_remaining,
    isUnlimited: input.is_unlimited,
  };
}

export function buildLoreLimitPrompt(
  tier: SubscriptionTier,
  currentCount: number,
  allowedCount: number,
  recommendedTier: SubscriptionTier,
) {
  return {
    reason: "lore_entry_limit_reached" as const,
    tier,
    currentCount,
    allowedCount,
    recommendedTier,
  };
}

export function parseLoreLimitError(message: string) {
  const [prefix, tier, currentCount, allowedCount, recommendedTier] = message.split("|");

  if (
    prefix !== "LORE_ENTRY_LIMIT_REACHED"
    || !tier
    || !currentCount
    || !allowedCount
    || !recommendedTier
  ) {
    return null;
  }

  return buildLoreLimitPrompt(
    tier as SubscriptionTier,
    Number(currentCount),
    Number(allowedCount),
    recommendedTier as SubscriptionTier,
  );
}

export function buildInitialActiveDocumentKind(): ProjectDocumentKind {
  return "governance";
}

export function getNextVisibleSelection(
  entries: LoreEntryView[],
  activeEntryId: string | null,
) {
  if (!activeEntryId) {
    return null;
  }

  return entries.some((entry) => entry.id === activeEntryId) ? activeEntryId : null;
}

export function getLoreStatusVariant(status: LoreCanonStatus) {
  if (status === "canon") {
    return "default";
  }

  if (status === "retconned") {
    return "archived";
  }

  return "secondary";
}
